<template>
  <form @submit.prevent="submit">
    <div class="row">
      <div class="col-md-12">
        <FormSelect
          name="department"
          v-model="fields.department"
          label="Department *"
          :errors="validation.fields.department.errors"
          :dirty="validation.fields.department.dirty"
          @change="onDepartmentChange"
          :disabled="loading"
          :testId="`${testId}-department-dropdown`"
        >
          <option value="" disabled data-test="department-select-none-option">None Selected</option>
          <option
            v-for="(department, index) in departments"
            :key="index"
            :value="department.id"
            :data-test="`department-select-${department.id}-option`"
          >
            {{ department.identity }}
          </option>
        </FormSelect>
      </div>
      <div class="col-md-12">
        <FormInput
          name="subject"
          v-model="fields.subject"
          label="Subject *"
          :errors="validation.fields.subject.errors"
          :dirty="validation.fields.subject.dirty"
          :disabled="loading"
          class="pb-2"
          :testId="`${testId}-subject-input`"
        />
      </div>
      <div class="col-md-12">
        <FormSelect
          name="service"
          v-model="fields.service"
          label="Service"
          :errors="validation.fields.service.errors"
          :dirty="validation.fields.service.dirty"
          :disabled="loading"
          :testId="`${testId}-service-dropdown`"
        >
          <option value="" disabled data-test="service-select-none-option">None Selected</option>
          <option
            v-for="(service, index) in ticketOptions.service"
            :key="index"
            :value="service.id"
            :data-test="`service-select-${service.id}-option`"
          >
            {{ service.identity }}
          </option>
        </FormSelect>
      </div>
      <div class="col-md-12">
        <FormEmailListInput
          name="recipients"
          v-model="fields.recipients"
          label="Recipients"
          :testId="testId"
        />
      </div>
      <div class="col-md-12">
        <FormTextArea
          name="reply"
          v-model="fields.reply"
          label="Comment *"
          :errors="validation.fields.reply.errors"
          :dirty="validation.fields.reply.dirty"
          :disabled="loading"
          class="pb-2"
          :testId="`${testId}-comment-textarea`"
        />
      </div>
      <div class="col-md-12">
        <FormSelect
          name="category"
          v-model="fields.category"
          label="Category"
          :disabled="loading"
          :testId="`${testId}-category-dropdown`"
        >
          <option value="" disabled data-test="category-select-none-option">None Selected</option>
          <option
            v-for="(category, index) in ticketOptions.categories"
            :key="index"
            :value="category.id"
            :data-test="`category-select-${category.id}-option`"
          >
            {{ category.identity }}
          </option>
        </FormSelect>
      </div>
      <template v-if="fields.customSupportOptions">
        <div class="col-md-12">
          <FormSelect
            v-if="(domainOptions?.length || 0) > 0"
            name="domain"
            v-model="fields.domain"
            :label="customFieldLabels.domain"
            :errors="validation.fields.domain.errors"
            :dirty="validation.fields.domain.dirty"
            :disabled="loading"
            :loading="domainOptionsLoading"
            class="pb-2"
            :testId="`${testId}-domain-dropdown`"
          >
            <option value="" data-test="domain-select-none-option">None Selected</option>
            <option
              v-for="(item, index) in domainOptions"
              :key="index"
              :value="item?.domain"
              :data-test="`domain-select-${index}-option`"
            >
              {{ item?.domain }}
            </option>
          </FormSelect>
          <FormInput
            v-else
            name="domain"
            v-model="fields.domain"
            :label="customFieldLabels.domain"
            :errors="validation.fields.domain.errors"
            :dirty="validation.fields.domain.dirty"
            :disabled="loading"
            :loading="domainOptionsLoading"
            class="pb-2"
            :testId="`${testId}-domain-input`"
          />
        </div>
        <div class="col-12 col-sm-6">
          <FormAutocompleteSelect
            name="phone"
            v-model="fields.phone"
            :disabled="loading"
            :label="customFieldLabels.phone"
            :options="fields.phoneOptions"
            :testId="`${testId}-phone-dropdown`"
          />
        </div>
        <div class="col-12 col-sm-6" v-if="isPhoneOther()">
          <FormInput
            name="phoneOther"
            v-model="fields.phoneOther"
            :label="customFieldLabels.phoneOther"
            :errors="validation.fields.phoneOther.errors"
            :dirty="validation.fields.phoneOther.dirty"
            :disabled="loading"
            class="pb-2"
            :testId="`${testId}-phone-other-input`"
          />
        </div>
      </template>
    </div>
    <div class="form-buttons">
      <Btn
        class="me-2"
        variant="secondary"
        type="button"
        :disabled="loading"
        @click="cancel"
        :testId="`${testId}-cancel-button`"
      >
        Cancel
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="!isSubmittable"
        :loading="loading"
        :testId="`${testId}-add-button`"
      >
        Add
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from "vue";
import Btn from "@/components/display/Btn";
import FormAutocompleteSelect from "@/components/display/form/FormAutocompleteSelect";
import FormEmailListInput from "@/components/display/form/FormEmailListInput";
import FormInput from "@/components/display/form/FormInput";
import FormSelect from "@/components/display/form/FormSelect";
import FormTextArea from "@/components/display/form/FormTextArea";
import { goToTicket } from "@/utils/useTickets";
import { models } from "leatherman-js";
import router from "@/router";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { validations as validationRules } from "leatherman-js";

export default {
  name: "ContactSupportForm",
  components: {
    Btn,
    FormAutocompleteSelect,
    FormEmailListInput,
    FormInput,
    FormSelect,
    FormTextArea,
  },
  emits: ["close", "confirm"],
  props: {
    testId: {
      type: String,
      default: "",
    },
    categoryId: String,
    departmentId: String,
    domain: String,
    forceSubmittable: {
      type: Boolean,
      default: false,
    },
    reply: String,
    serviceId: Number,
    subject: String,
    ticketOptions: Object,
  },
  setup(props, { emit }) {
    const SUPPORT = "support";
    const OTHER = "Other";

    const addresses = computed(() => store.getters["addresses/getList"]);

    const getPhoneOptions = computed(() =>
      addresses.value.map((address) => address.phone).concat(OTHER)
    );

    const domainOptions = ref(null);
    const domainOptionsLoading = ref(false);

    const loading = ref(false);
    const errors = ref(null);

    const fields = reactive({
      department: props.departmentId,
      subject: props.subject,
      service: props.serviceId,
      recipients: [],
      reply: props.reply,
      category: props.categoryId,
      customSupportOptions: props.departmentId === SUPPORT,
      domain: props.departmentId === SUPPORT ? props.domain : null,
      phone: null,
      phoneOther: null,
      phoneOptions: getPhoneOptions,
    });

    const customFieldLabels = {
      domain: "Domain",
      phone: "Phone Number",
      phoneOther: "Phone (Other)",
    };

    const departments = computed(() =>
      props.ticketOptions.departments.filter((item) => item.id !== "migrations")
    );

    const onDepartmentChange = () => {
      fields.customSupportOptions = fields.department === SUPPORT;
      Object.keys(customFieldLabels).forEach((key) => {
        fields[key] = null;
      });
    };

    const isPhoneOther = () => fields.phone === OTHER;

    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules.forms.addTicket,
      {},
      {
        department: {
          required: "Department is Required",
        },
        subject: {
          required: "Subject is Required",
          minChars: "Subject line must be longer than 2 characters.",
          maxChars: "Subject line must be shorter than 250 characters.",
        },
        reply: {
          required: "Comment is Required",
          minChars: "Comment must be greater than one character.",
          maxChars: "Comment must be less than 4000 characters.",
        },
        phoneOther: {
          phone: "Invalid Phone Number",
        },
        domain: {
          domain: "Invalid Domain",
        },
      }
    );

    const uploadingFiles = ref(false);

    const cancel = () => emit("close");
    const isSubmittable = computed(() => {
      return (
        !uploadingFiles.value &&
        (props.forceSubmittable ||
          (!loading.value &&
            validation.errors.length === 0 &&
            (validation.fields.department.dirty ||
              validation.fields.subject.dirty ||
              validation.fields.service.dirty ||
              validation.fields.recipients.dirty ||
              validation.fields.reply.dirty ||
              validation.fields.category.dirty)))
      );
    });

    watch(fields, (sel) => {
      if (
        process.env.VUE_APP_SUPPORT_MIGRATION_DEPARTMENT &&
        sel.department === process.env.VUE_APP_SUPPORT_MIGRATION_DEPARTMENT
      ) {
        router.push({ name: "request-migration" }).catch(() => {});
        cancel();
      }
    });

    watch(
      () => fields.service,
      (value) => {
        domainOptionsLoading.value = true;
        fields.domain = null;
        store
          .dispatch("sites/domains/getListByServiceId", value)
          .then((domains) => (domainOptions.value = (domains?.length || 0) > 0 ? domains : null))
          .finally(() => (domainOptionsLoading.value = false));
      }
    );

    onMounted(() => {
      store.dispatch("addresses/getList");
      if (props.serviceId) {
        domainOptionsLoading.value = false;
        store
          .dispatch("sites/domains/getListByServiceId", props.serviceId)
          .then((domains) => (domainOptions.value = (domains?.length || 0) > 0 ? domains : null))
          .finally(() => (domainOptionsLoading.value = false));
      }
    });

    const submit = () => {
      errors.value = null;
      fields.reply = models.Ticket.ParseCustomFields(customFieldLabels, fields, fields.reply);
      validation
        .validate()
        .then(() => {
          if (fields.recipients.length === 0) {
            fields.recipients = null;
          }
          loading.value = true;
          store
            .dispatch("tickets/create", fields)
            .then((response) => {
              store.dispatch("toasts/addToast", {
                variant: "success",
                message: "Successfully created ticket.",
              });
              emit("confirm");
              goToTicket(response.id);
            })
            .catch(() =>
              store.dispatch("toasts/addToast", {
                variant: "error",
                message:
                  "Something went wrong when creating your ticket. For immediate help, please contact our support team via chat or phone.",
                timeout: true,
              })
            )
            .finally(() => {
              loading.value = false;
            });
        })
        .catch(() => {});
    };
    return {
      addresses,
      cancel,
      customFieldLabels,
      departments,
      domainOptions,
      domainOptionsLoading,
      errors,
      fields,
      isPhoneOther,
      isSubmittable,
      loading,
      onDepartmentChange,
      submit,
      uploadingFiles,
      validation,
    };
  },
};
</script>
